const React = require('react');
const { default: Styled } = require('styled-components');
const AutocompleteField = require('./AutocompleteField');
const SelectField = require('./SelectField');
const SwitchField = require('./SwitchField');
const FieldContext = require('./FieldContext');

const {
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} = React;

const internals = {};

internals.PLACE_TYPES = [
    'locality',
    'sublocality',
    'neighborhood',
    'administrative_area_level_3',
    'colloquial_area'
];

internals.isOrgLike = (formProps = {}) => !!(
    formProps.isCompany
    || formProps.isCommunity
    || formProps.isSchoolOnline
);

internals.buildEmptyValue = (onCampus = false) => ({
    id: null,
    placeId: null,
    name: null,
    onCampus
});

internals.buildValue = ({
    option,
    onCampus = false
}) => {

    if (!option) {
        return internals.buildEmptyValue(onCampus);
    }

    return {
        id: option.reference ? null : (option.id ?? null),
        placeId: option.place_id || option.placeId || null,
        name: option.description || option.name || null,
        onCampus
    };
};

internals.getOptionValue = (option) => option?.place_id || option?.placeId || option?.id || option?.description || option?.name || '';
internals.getOptionLabel = (option) => option?.description || option?.name || '';
internals.getSelectedValue = (value) => value?.placeId || value?.id || value?.name || '';

internals.normalizePlaceOption = (option = {}) => {

    if (!option || typeof option !== 'object') {
        return option;
    }

    if (option.description || option.place_id) {
        return option;
    }

    return {
        ...option,
        description: option.name,
        place_id: option.placeId
    };
};

internals.getOptionsFromSource = ({
    formProps = {},
    source
}) => {

    if (Array.isArray(source)) {
        return source;
    }

    if (typeof source === 'string' && Array.isArray(formProps[source])) {
        return formProps[source];
    }

    return [];
};

internals.getOffCampusOptions = ({
    field = {},
    formProps = {}
}) => internals.getOptionsFromSource({
    formProps,
    source: field.offCampusOptions || field.offCampusOptionsSource || 'offCampusHousingList'
}).map(internals.normalizePlaceOption);

internals.getOnCampusOptions = ({
    field = {},
    formProps = {}
}) => internals.getOptionsFromSource({
    formProps,
    source: field.onCampusOptions || field.onCampusOptionsSource || 'onCampusHousingList'
});

internals.getGoogleAutocompleteService = () => {

    if (typeof window === 'undefined' || !window.google?.maps?.places?.AutocompleteService) {
        return null;
    }

    return new window.google.maps.places.AutocompleteService();
};

internals.buildPlaceDataSource = ({
    field = {},
    formProps = {}
}) => {

    const autocompleteService = internals.getGoogleAutocompleteService();
    const staticOptions = internals.getOffCampusOptions({
        field,
        formProps
    });
    const types = field.placeTypes || formProps.placeTypes || internals.PLACE_TYPES;

    return ({ query }) => new Promise((resolve) => {

        if (!query || !query.length || !autocompleteService || typeof window === 'undefined' || !window.google) {
            resolve(staticOptions);
            return;
        }

        autocompleteService.getPlacePredictions({
            input: query,
            location: new window.google.maps.LatLng(0, 0),
            radius: 0,
            types
        }, (data = []) => {
            resolve(staticOptions.concat(data || []));
        });
    });
};

internals.buildOnCampusSelectOptions = (onCampusOptions = []) => [
    { value: '', label: 'Select...' },
    ...onCampusOptions.map((option) => ({
        value: option.name,
        label: option.name
    }))
];

internals.resolveOffCampusLabel = ({
    field = {},
    formProps = {}
}) => {

    if (internals.isOrgLike(formProps)) {
        return field.orgLikeLabel || 'Where I Live';
    }

    return field.offCampusLabel || 'Off-campus location:';
};

module.exports = function CampusPlaceField(props) {

    const {
        field = {},
        fieldName = field.name,
        textFieldProps = {}
    } = props;

    const valueField = FieldContext.useField(fieldName);
    const textField = FieldContext.useField(field.textFieldName || `${fieldName}Text`);
    const formProps = valueField.formProps || {};
    const value = valueField.getValue() || internals.buildEmptyValue(false);
    const textValue = textField.getValue() || '';
    const hasError = valueField.hasError?.() || false;
    const helperText = valueField.getHelperText?.() || null;
    const isOrgLike = internals.isOrgLike(formProps);
    const locationInputRef = useRef(null);
    const [selectedValueErrorShow, setSelectedValueErrorShow] = useState(false);
    const dataSource = useMemo(() => internals.buildPlaceDataSource({
        field,
        formProps
    }), [field, formProps]);
    const onCampusOptions = useMemo(() => internals.getOnCampusOptions({
        field,
        formProps
    }), [field, formProps]);
    const selectOptions = useMemo(() => internals.buildOnCampusSelectOptions(onCampusOptions), [onCampusOptions]);

    const validate = () => {

        if (!textValue || value?.name) {
            return null;
        }

        return field.listChoiceErrorMessage || 'Please choose an item in the list';
    };

    useImperativeHandle(valueField.inputRef, () => ({
        inputRef: locationInputRef,
        validate,
        validateAndMaybeShowErr: () => {

            if (validate()) {
                setSelectedValueErrorShow(true);
            }
        }
    }), [valueField.inputRef, textValue, value]);

    const syncValue = (nextValue) => {

        valueField.setValue(nextValue);
        textField.setValue(nextValue?.name || '');
    };

    const clearValue = (onCampus = false) => {

        syncValue(internals.buildEmptyValue(onCampus));
        setSelectedValueErrorShow(false);
    };

    const resolvedHelperText = helperText || (selectedValueErrorShow ? validate() : null);
    const resolvedError = hasError || !!resolvedHelperText;

    return <internals.FieldRoot className={field.className || 'campusPlaceField'}>
        {!isOrgLike && <SwitchField
            label={field.switchLabel || 'Do you live on campus?'}
            field={{
                offLabel: field.switchOffLabel || 'No/Skip',
                onLabel: field.switchOnLabel || 'Yes'
            }}
            value={!!value?.onCampus}
            onChange={(nextValue) => {
                clearValue(nextValue);
            }}
            switchProps={{
                id: field.switchId || `${fieldName}-campus-toggle`
            }}
        />}

        {!value?.onCampus && <AutocompleteField
            clearValue={internals.buildEmptyValue(false)}
            dataSource={dataSource}
            field={field}
            fieldName={fieldName}
            fieldValueFromOption={(option) => internals.buildValue({
                option,
                onCampus: false
            })}
            getOptionLabel={internals.getOptionLabel}
            getOptionValue={internals.getOptionValue}
            getSelectedValue={internals.getSelectedValue}
            label={internals.resolveOffCampusLabel({
                field,
                formProps
            })}
            onBlur={() => {

                if (!validate()) {
                    setSelectedValueErrorShow(false);
                }
            }}
            onFocus={() => {
                setSelectedValueErrorShow(false);
            }}
            suppressEmptyState
            textFieldName={field.textFieldName || `${fieldName}Text`}
            textFieldProps={{
                ...textFieldProps,
                error: resolvedError,
                helperText: resolvedHelperText,
                inputRef: locationInputRef,
                placeholder: field.placeholder || 'Type to search...'
            }}
            throttleMs={field.throttleMs || 250}
        />}

        {!!value?.onCampus && <SelectField
            field={{
                ...field,
                label: field.onCampusLabel || 'On-campus location:',
                options: selectOptions
            }}
            fieldName={fieldName}
            setValue={(nextValue) => {

                if (!nextValue) {
                    clearValue(true);
                    return;
                }

                const selectedOption = onCampusOptions.find(({ name }) => name === nextValue);

                if (!selectedOption) {
                    clearValue(true);
                    return;
                }

                syncValue({
                    id: selectedOption.id,
                    placeId: selectedOption.placeId,
                    name: selectedOption.name,
                    onCampus: true
                });
                setSelectedValueErrorShow(false);
            }}
            textFieldProps={{
                ...textFieldProps,
                error: resolvedError,
                helperText: resolvedHelperText,
                inputRef: locationInputRef
            }}
            value={value?.name || ''}
        />}
    </internals.FieldRoot>;
};

internals.FieldRoot = Styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    width: 100%;
`;

module.exports.internals = internals;
